import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { useTheme, getColors } from "../ThemeContext";
import { useAIAdvice } from "../useAIAdvice";
import { useWell } from "../WellContext";
import {
  Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, Cell,
  BarChart, Bar, LineChart,
} from "recharts";
import { API } from "../config/api";
import PlotSummary from "../components/PlotSummary";
const C = {
  bg:"#050b14",panel:"#080f1e",card:"#0b1525",border:"#0f2040",border2:"#162a4a",
  cyan:"#00d4ff",teal:"#00ffcc",amber:"#ffb84d",pink:"#ff4da6",purple:"#a855f7",
  blue:"#3b9eff",red:"#ff3d5a",green:"#00ff88",orange:"#ff7c2a",lime:"#b0ff4a",
  text:"#e2eaf8",muted:"#4a6080",muted2:"#2a3f60",
};
function applyTheme(colors) { Object.assign(C, colors); }
const MONO = "'Space Mono', monospace";
const SANS = "'Syne', sans-serif";
const PLOT_GAP_PX = 32;   // space between plots, cards and sections


const FIELD_META = {
  ArrivalVelocity: {
    label: "Plunger Arrival Velocity", unit: "ft/min",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "No data" };
      if (v < 350)   return { color: C.red,   text: `TOO SLOW (${v.toFixed(0)} ft/min), below safe min 350. Extend shut-in.` };
      if (v > 1200)  return { color: C.amber, text: `TOO FAST (${v.toFixed(0)} ft/min), above 1200. Reduce casing pressure target.` };
      return           { color: C.green, text: `SAFE (${v.toFixed(0)} ft/min), optimal plunger travel range.` };
    },
    advice: v => v < 350 ? "▽ Too slow (<350 ft/min), plunger may drop back. Extend shut-in time or reduce line pressure." : v > 1200 ? "▶ Too fast (>1200 ft/min), excess energy. Reduce shut-in time to save gas." : "◆ Optimal range (350–1200 ft/min). Perfect lift!",
  },
  FlowRateAtClose: {
    label: "Gas Production Rate (at valve close)", unit: "MCF/day",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "No data" };
      if (v < 50)   return { color: C.red,   text: `LOW (${v.toFixed(1)} MCF/day), well under-producing. Check liquid loading.` };
      return          { color: C.teal,  text: `${v.toFixed(1)} MCF/day, gas sold at valve close. Higher = more revenue.` };
    },
    advice: v => v < 50 ? "▲ Low flow rate, well may be under-performing. Check valve, plunger, separator." : v > 500 ? "◆ Excellent production rate!" : "◆ Normal flow rate.",
  },
  CsgAtOpen: {
    label: "Casing Pressure at Valve Open", unit: "psi",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "No data" };
      if (v < 300)   return { color: C.red,   text: `LOW (${v.toFixed(0)} psi), not enough reservoir drive to push plunger up.` };
      return           { color: C.cyan,  text: `${v.toFixed(0)} psi, reservoir pressure driving plunger upward.` };
    },
    advice: v => v < 300 ? "▲ Low, may not have enough gas to lift plunger. Increase shut-in time." : v > 800 ? "◆ High, good reservoir pressure. Consider opening valve sooner." : "◆ Normal range. Monitor trends.",
  },
  LoadFactor: {
    label: "Load Factor", unit: "",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "No data" };
      if (v >= 0.5)  return { color: C.red,   text: `OVERLOADED (${v.toFixed(3)}), plunger WILL NOT arrive. Extend shut-in NOW.` };
      if (v >= 0.35) return { color: C.amber, text: `APPROACHING LIMIT (${v.toFixed(3)}), close to 0.5 threshold, monitor.` };
      return           { color: C.green, text: `SAFE (${v.toFixed(3)}), plunger can lift this liquid column.` };
    },
    advice: v => v >= 0.5 ? "■ Load factor ≥0.5, plunger cannot lift liquid. Extend shut-in significantly before opening." : v >= 0.35 ? "▲ Load factor approaching 0.5, add 10–15 min to shut-in time as precaution." : "◆ Load factor safe. Plunger can lift current liquid column.",
  },
  TimeOffTotal: {
    label: "Shut-In Duration", unit: "min",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "No data" };
      return { color: C.teal, text: `${v.toFixed(0)} min shut-in, valve closed, casing pressure rebuilding.` };
    },
    advice: v => v < 30 ? "▲ Short shut-in, casing may not build enough pressure. Increase to 45–90 min." : v > 300 ? "▲ Very long shut-in, wasting production time. Reduce if casing pressure is sufficient." : "◆ Shut-in time is in good range.",
  },
  TimeAfterFlow: {
    label: "Afterflow Duration", unit: "min",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "No data" };
      return { color: C.teal, text: `${v.toFixed(0)} min afterflow, this is your gas production window.` };
    },
    advice: v => v < 10 ? "▲ Short after-flow, closing valve too early. Extend to capture more gas." : v > 120 ? "▲ Very long after-flow, may delay next cycle." : "◆ Good after-flow window.",
  },
  score: {
    label: "Cycle Health Score", unit: "/100",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "No score" };
      if (v >= 75)  return { color: C.green, text: `EXCELLENT (${v}), all plunger physics operating within spec.` };
      if (v >= 55)  return { color: C.green, text: `GOOD (${v}), minor deviations, within acceptable range.` };
      if (v >= 35)  return { color: C.amber, text: `WARNING (${v}), multiple parameters out of spec. Review cycle.` };
      return          { color: C.red,   text: `CRITICAL (${v}), serious issues. Check velocity, load factor, arrival.` };
    },
    advice: v => v >= 75 ? "◆ Excellent cycle score, well operating within all physics limits." : v >= 55 ? "◆ Good score, minor deviations. Monitor trend for deterioration." : v >= 35 ? "▲ Warning, multiple parameters out of spec. Review velocity and load factor." : "■ Critical score, serious issues detected. Check plunger arrival and casing pressure.",
  },
  anomaly_score: {
    label: "Anomaly Score", unit: "/10",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "No score" };
      if (v < 3.33) return { color: C.green, text: `GOOD (${v.toFixed(1)}), cycle within expected operating range.` };
      if (v < 6.66) return { color: C.amber, text: `WARNING (${v.toFixed(1)}), some parameters deviated from normal.` };
      return          { color: C.red,   text: `CRITICAL (${v.toFixed(1)}), unusual cycle, investigate triggers below.` };
    },
    advice: v => v < 3.33 ? "◆ Good, all parameters within expected statistical range. No action needed." : v < 6.66 ? "▲ Warning, one or more parameters deviated. Check triggered features and compare to prior cycles." : "■ Critical, very unusual cycle. Investigate triggered parameters immediately.",
  },
  // FIXED
  violations: {
    label: "Physics Violations", unit: "count",
    interpret: v => {
      if (v == null || v === 0) return { color: C.green, text: "0 violations, cycle passed all physics equations." };
      if (v === 1)              return { color: C.amber, text: `WARNING: 1 violation, one physics equation failed this cycle.` };
      return                             { color: C.red,   text: `CRITICAL: ${v} violations, multiple physics failures. Cycle is out of spec.` };
    },
    advice: v => (!v || v === 0) ? "◆ Zero violations, well operating within all physics limits this cycle." : v === 1 ? "▲ Warning, one physics violation. Check load factor and casing pressure at open." : `■ Critical, ${v} violations. Multiple physics failures. Review load factor, velocity, and shut-in timing.`,
  },
  actual: {
    label: "Actual Production", unit: "MCF/day",
    interpret: v => v != null ? { color: C.teal, text: `Actual measured: ${v.toFixed(2)} MCF/day` } : { color: C.muted, text: "No data" },
    advice: v => v != null ? (v < 50 ? "▲ Low production rate, check for liquid loading or valve issues." : "◆ Normal production rate for this cycle.") : null,
  },
  forecast: {
    label: "Deep Sequence Neural Network Forecast", unit: "MCF/day",
    interpret: v => v != null ? { color: C.cyan, text: `Model predicts ${v.toFixed(2)} MCF/day for this future cycle.` } : { color: C.muted, text: "No forecast" },
    advice: v => v != null ? `Model forecasts ${v.toFixed(1)} MCF/day. Compare to your historical average to assess trajectory.` : null,
  },
  upper_80: {
    label: "Upper 80% Confidence", unit: "MCF/day",
    interpret: v => v != null ? { color: C.cyan, text: `Optimistic bound: ${v.toFixed(2)} MCF/day (top 10% of outcomes).` } : { color: C.muted, text: "N/A" },
    advice: () => "Upper bound of the 80% confidence interval, actual production will exceed this only 10% of the time.",
  },
  lower_80: {
    label: "Lower 80% Confidence", unit: "MCF/day",
    interpret: v => v != null ? { color: C.blue, text: `Conservative bound: ${v.toFixed(2)} MCF/day (bottom 10% of outcomes).` } : { color: C.muted, text: "N/A" },
    advice: () => "Lower bound of the 80% confidence interval, actual production falls below this only 10% of the time.",
  },
  rolling_avg: {
    label: "Rolling Average Score", unit: "/100",
    interpret: v => v != null ? { color: C.cyan, text: `Smoothed trend: ${v.toFixed(1)}/100 over recent cycles.` } : { color: C.muted, text: "N/A" },
    advice: v => v != null ? (v < 55 ? "▲ Rolling average below 55, sustained performance issues. Review shut-in setpoints." : "◆ Rolling average healthy, no sustained degradation trend.") : null,
  },
  significance: {
    label: "Change Significance (σ)", unit: "σ",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "N/A" };
      if (v > 1.5)  return { color: C.red,   text: `CRITICAL High shift (${v.toFixed(2)}σ). This parameter changed significantly vs prior period.` };
      if (v > 0.8)  return { color: C.amber, text: `WARNING Moderate shift (${v.toFixed(2)}σ). Noticeable change, monitor closely.` };
      return          { color: C.green, text: `GOOD Low shift (${v.toFixed(2)}σ). Minor variation, within normal range.` };
    },
    advice: v => v > 1.5 ? "■ Critical, this parameter shifted substantially. Investigate whether it was a setpoint change or a well condition change." : v > 0.8 ? "▲ Warning, moderate shift detected. Monitor over next 5–10 cycles to confirm trend." : "◆ Good, within normal cycle-to-cycle variation. No action needed.",
  },
  arr: {
    label: "Arrival Time", unit: "min",
    interpret: v => {
      if (v == null) return { color: C.muted, text: "No arrival data" };
      if (v > 45) return { color: C.red,   text: `CRITICAL (${Number(v).toFixed(1)} min), very slow arrival.` };
      if (v > 25) return { color: C.amber, text: `WARNING (${Number(v).toFixed(1)} min), slower than preferred.` };
      return        { color: C.green, text: `GOOD (${Number(v).toFixed(1)} min), arrival in expected window.` };
    },
    advice: v => v == null ? null : v > 45 ? "■ Critical slow arrival, check liquid load and shut-in energy." : v > 25 ? "▲ Warning, arrival trending slow. Review build-up." : "◆ Good arrival time.",
  },
  csg: {
    label: "Casing Pressure", unit: "psi",
    interpret: v => v == null ? { color: C.muted, text: "No CP data" } : { color: C.cyan, text: `Casing pressure ${Number(v).toFixed(1)} psi.` },
    advice: () => "Review casing pressure vs recent baseline for step changes or drift.",
  },
  tbg: {
    label: "Tubing Pressure", unit: "psi",
    interpret: v => v == null ? { color: C.muted, text: "No TP data" } : { color: C.cyan, text: `Tubing pressure ${Number(v).toFixed(1)} psi.` },
    advice: () => "Compare tubing vs line pressure when diagnosing surface restriction.",
  },
  line: {
    label: "Line Pressure", unit: "psi",
    interpret: v => v == null ? { color: C.muted, text: "No line data" } : { color: C.muted, text: `Line pressure ${Number(v).toFixed(1)} psi.` },
    advice: () => "Line pressure is the surface reference for restriction and leakage checks.",
  },
  hit: {
    label: "Flagged Event", unit: "",
    interpret: v => v == null ? { color: C.green, text: "GOOD, no anomaly flag on this cycle." } : { color: C.red, text: `CRITICAL, anomaly marker on this cycle.` },
    advice: v => v == null ? "◆ Good, cycle not flagged." : "■ Critical flagged cycle, review evidence and recommended action.",
  },
  count: {
    label: "Cycle Count", unit: "cycles",
    interpret: v => v != null ? { color: C.muted, text: `${v} cycles in this operating range.` } : { color: C.muted, text: "N/A" },
    advice: v => v > 50 ? "◆ High frequency, this operating range is most common for well." : v < 5 ? "▲ Rare operating range, outlier conditions. Investigate what drives these cycles." : "◆ Normal distribution bin.",
  },
};

const GENERIC_KEYS = new Set([
  "count","value","bin","actual","lstm","target","step",
  "violations","score","anomaly_score","rolling_avg",
  "significance","forecast","upper_80","lower_80",
  "arr","csg","tbg","line","hit","diff","i",
]);

/** Full display names for chart series keys (legend / tooltip). */
const SERIES_ALIASES = {
  arr: "arr",
  arrival: "arr",
  ArrivalTime: "arr",
  lh: "LoadFactor",
  load: "LoadFactor",
  load_factor: "LoadFactor",
  LoadFactor: "LoadFactor",
  flow: "FlowRateAtClose",
  FlowRate: "FlowRateAtClose",
  FlowRateAtClose: "FlowRateAtClose",
  csg: "csg",
  casing: "csg",
  CsgAtOpen: "CsgAtOpen",
  tbg: "tbg",
  tubing: "tbg",
  line: "line",
  velocity: "ArrivalVelocity",
  ArrivalVelocity: "ArrivalVelocity",
  TimeOffTotal: "TimeOffTotal",
  TimeAfterFlow: "TimeAfterFlow",
  score: "score",
  anomaly_score: "anomaly_score",
};

/* Complete legend names (with units) for the root-cause trend chart series keys */
const SERIES_LEGEND = {
  arr:     "Plunger Arrival Time (min)",
  vel:     "Plunger Arrival Velocity (ft/min)",
  timeoff: "Shut-In (Off) Time (min)",
  lh:      "Liquid Height (ft)",
  csg:     "Casing Pressure (psi)",
  tbg:     "Tubing Pressure (psi)",
  line:    "Line Pressure (psi)",
  bu:      "Pressure Build-up Rate (psi/min)",
  flow:    "Gas Production Rate at Valve Close (MCF/day)",
};

function seriesLabel(key) {
  if (key == null) return "";
  const k = String(key);
  if (SERIES_LEGEND[k]) return SERIES_LEGEND[k];
  const metaKey = SERIES_ALIASES[k] || k;
  const meta = FIELD_META[metaKey] || FIELD_META[k];
  if (meta?.label) return meta.label;
  // fallback: prettify snake/camel
  return k
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}


function SmartTooltip({ active, payload, label, note }) {
  const { advice, loading, fetchAdvice, clearAdvice } = useAIAdvice();
  const prevKeyRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  useEffect(() => {
    function onMove(e) { setMousePos({ x: e.clientX, y: e.clientY }); }
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  const raw      = active && payload?.length ? payload[0]?.payload || {} : {};
  const cycleNum = raw.CycleSerialNumber ?? raw.cycle ?? raw.index ?? null;
  const timeStr  = raw.time || raw.timestamp || raw.date || null;
  const fieldKey = raw._actualFieldKey || null;

  let primaryField = null;
  let primaryValue = null;
  let primaryUnit  = "";

  const allFields = [];

  if (active && payload?.length) {
    for (const p of payload) {
      const rawKey    = p.dataKey || p.name || "";
      // Prefer explicit chart field key when series name is generic
      let lookupKey = rawKey;
      if ((GENERIC_KEYS.has(rawKey) || !FIELD_META[rawKey]) && fieldKey) {
        lookupKey = fieldKey;
      }
      const meta      = FIELD_META[lookupKey] || FIELD_META[rawKey] || null;
      const val       = typeof p.value === "number" ? p.value : (p.value != null && !isNaN(Number(p.value)) ? Number(p.value) : null);

      if (val !== null && rawKey !== "i") {
        const pointStatus = String(raw.status || "").toLowerCase();
        let interp = meta?.interpret?.(val);
        if (pointStatus === "critical" || rawKey === "hitCritical") {
          interp = { color: C.red, text: "CRITICAL, flagged anomaly on this cycle." };
        } else if (pointStatus === "warning" || rawKey === "hitWarning") {
          interp = { color: C.amber, text: "WARNING, flagged anomaly on this cycle." };
        } else if (pointStatus === "good" && (raw.hit != null || rawKey === "hitGood")) {
          interp = { color: C.green, text: "GOOD, info-level marker on this cycle." };
        }
        allFields.push({
          field: lookupKey || rawKey,
          label: meta?.label || rawKey,
          value: Math.round(val * 100) / 100,
          unit:  meta?.unit || "",
          status: interp?.text || "",
        });
        // Always set a primary field so AI advice is requested (even without FIELD_META)
        if (primaryField === null) {
          primaryField = lookupKey || rawKey;
          primaryValue = val;
          primaryUnit  = meta?.unit || "";
        }
      }
    }
  }

  const context = {};
  Object.entries(raw).forEach(([k, v]) => {
    if (typeof v === "number" && !k.startsWith("_") && k !== "index") {
      context[k] = Math.round(v * 100) / 100;
    }
  });
  if (cycleNum != null) context.cycle_num = cycleNum;

  useEffect(() => {
    if (!active || !primaryField || primaryValue == null) return;
    const rounded  = typeof primaryValue === "number"
      ? Math.round(primaryValue * 10) / 10 : primaryValue;
    const allValsKey = allFields.map(f => `${f.field}:${f.value}`).join("|");
    const key = `${primaryField}_${rounded}_${allValsKey}`;
    if (prevKeyRef.current === key) return;
    prevKeyRef.current = key;
    fetchAdvice(primaryField, primaryValue, primaryUnit, cycleNum, context, allFields);
  }, [active, primaryField, primaryValue, JSON.stringify(allFields)]);

  useEffect(() => {
    if (!active) { clearAdvice(); }
  }, [active]);

  if (!active || !payload?.length) return null;
  const TIP_W = 360;
  const TIP_H = 320;
  const GAP   = 14;
  const vw    = window.innerWidth;
  const vh    = window.innerHeight;
  let left    = mousePos.x + GAP;
  let top     = mousePos.y + GAP;
  if (left + TIP_W > vw - 8) left = mousePos.x - TIP_W - GAP;
  if (top  + TIP_H > vh - 8) top  = mousePos.y - TIP_H - GAP;

  const cycleLabel = label != null
    ? (isNaN(Number(String(label))) ? String(label) : `Cycle ${label}`)
    : (cycleNum != null ? `Cycle ${cycleNum}` : null);

  const tooltipContent = (
    <div style={{
      position: "fixed", left, top,
      background: C.card, border: `1px solid ${C.border2}`, borderRadius: 12,
      padding: "12px 16px", fontFamily: MONO, fontSize: 11,
      boxShadow: "0 12px 40px #000e", maxWidth: TIP_W,
      zIndex: 99999, pointerEvents: "none",
    }}>
      {/* Header */}
      {(cycleLabel || timeStr || note) && (
        <div style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: 7, marginBottom: 8 }}>
          {cycleLabel && <div style={{ color: C.cyan, fontWeight: 700, fontSize: 12, marginBottom: 2 }}>{cycleLabel}</div>}
          {timeStr    && <div style={{ color: C.muted, fontSize: 9 }}>{timeStr}</div>}
          {note       && <div style={{ color: C.muted, fontSize: 9, marginTop: 2 }}>{note}</div>}
        </div>
      )}

      {/* Series rows */}
      {payload.map((p, i) => {
        const rawKey    = p.dataKey || p.name || "";
        const isGeneric = GENERIC_KEYS.has(rawKey);
        const lookupKey = isGeneric && fieldKey ? fieldKey : rawKey;
        const meta      = FIELD_META[lookupKey] || FIELD_META[rawKey];

        let displayName;
        if      (rawKey === "actual")        displayName = meta?.label ? `Actual, ${meta.label}` : "Actual";
        else if (rawKey === "lstm")          displayName = meta?.label ? `Deep Seq NN, ${meta.label}` : "LSTM";
        else if (rawKey === "target")        displayName = "Target";
        else if (rawKey === "rolling_avg")   displayName = "Rolling Average";
        else if (rawKey === "forecast")      displayName = "Deep Seq NN Forecast";
        else if (rawKey === "upper_80")      displayName = "Upper 80% CI";
        else if (rawKey === "lower_80")      displayName = "Lower 80% CI";
        else if (rawKey === "violations")    displayName = "Physics Violations";
        else if (rawKey === "anomaly_score") displayName = "Anomaly Score";
        else if (rawKey === "significance")  displayName = "Significance (σ)";
        else if (rawKey === "score")         displayName = meta?.label || "Health Score";
        else if (rawKey === "count")         displayName = meta?.label || "Cycle Count";
        else                                 displayName = meta?.label || p.name || rawKey;
        let tooltipColor = p.color || C.cyan;
        if (rawKey === "lower_80") tooltipColor = C.blue;

        const isCountBar = rawKey === "count";
        const unit       = isCountBar ? "cycles" : (meta?.unit || "");
        const val        = p.value;
        const formatted  = typeof val === "number"
          ? (Math.abs(val) >= 100 ? val.toFixed(1) : val.toFixed(2))
          : String(val ?? "N/A");
        // Prefer plot severity from the data point when present so red markers
        // always show CRITICAL (not absolute threshold GOOD/WARNING).
        const pointStatus = String(raw.status || "").toLowerCase();
        const isFlagHit = ["hit", "hitCritical", "hitWarning", "hitGood"].includes(rawKey)
          || raw.hit != null || raw.hitCritical != null || raw.hitWarning != null || raw.hitGood != null;
        let interp = meta?.interpret ? meta.interpret(val) : null;
        const typeLabel = (raw.anomaly_type || "anomaly").replace(/_/g, " ");
        const valTxt = typeof val === "number" ? (Math.abs(val)>=100?val.toFixed(1):val.toFixed(2)) : val;
        if (pointStatus === "critical" || rawKey === "hitCritical") {
          interp = { color: C.red, text: `CRITICAL, flagged ${typeLabel} on this cycle (${valTxt}${unit ? " " + unit : ""}).` };
          tooltipColor = C.red;
        } else if (pointStatus === "warning" || rawKey === "hitWarning") {
          interp = { color: C.amber, text: `WARNING, flagged ${typeLabel} on this cycle (${valTxt}${unit ? " " + unit : ""}).` };
          tooltipColor = C.amber;
        } else if ((pointStatus === "good" || rawKey === "hitGood") && isFlagHit) {
          interp = { color: C.green, text: `GOOD, minor / info-level ${typeLabel} marker (${valTxt}${unit ? " " + unit : ""}).` };
          tooltipColor = C.green;
        } else if (interp?.color) {
          tooltipColor = interp.color;
        }

        return (
          <div key={i} style={{ marginBottom: i < payload.length - 1 ? 10 : 0 }}>
            <div style={{
              display: "flex", justifyContent: "space-between",
              alignItems: "center", gap: 12,
              paddingBottom: interp ? 4 : 0,
            }}>
              <span style={{ color: C.muted, fontSize: 10, flex: 1, lineHeight: 1.3 }}>
                {displayName}
              </span>
              <span style={{ textAlign: "right", flexShrink: 0 }}>
                <span style={{ color: tooltipColor, fontWeight: 700, fontSize: 12 }}> 
                  {formatted}
                </span>
                {unit && (
                  <span style={{ color: C.muted, fontSize: 9, marginLeft: 3 }}>{unit}</span>
                )}
              </span>
            </div>
            {interp && (
              <div style={{
                marginTop: 3, padding: "4px 8px",
                background: `${interp.color}12`,
                border: `1px solid ${interp.color}33`,
                borderRadius: 6, fontSize: 10,
                color: interp.color, lineHeight: 1.5,
              }}>
                {interp.text}
              </div>
            )}
            {i < payload.length - 1 && (
              <div style={{ height: 1, background: C.border, marginTop: 8 }} />
            )}
          </div>
        );
      })}

      {/* AI Recommendation */}
      <div style={{
        marginTop: 10, borderTop: `1px solid ${C.border}`,
        fontSize: 10, lineHeight: 1.6,
        background: loading ? `${C.muted}08` : `${C.cyan}08`,
        borderRadius: 6, padding: "6px 8px",
      }}>
        {loading
          ? <span style={{ color: C.muted }}>Generating AI Recommendations…</span>
          : advice
            ? renderAdvice(advice)
            : <span style={{ color: C.muted }}>Hover a data point for AI advice</span>
        }
      </div>
    </div>
  );
  return ReactDOM.createPortal(tooltipContent, document.body);
}
const TTip = SmartTooltip;

function renderAdvice(text) {
  if (!text) return null;
  const ICONS = {
    "▲": (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}>
        <path d="M6.5 1.5L11.8 10.5H1.2L6.5 1.5Z" stroke="#f59e0b" strokeWidth="1.4" fill="#f59e0b22" strokeLinejoin="round"/>
        <line x1="6.5" y1="4.5" x2="6.5" y2="7.5" stroke="#f59e0b" strokeWidth="1.4" strokeLinecap="round"/>
        <circle cx="6.5" cy="9.2" r="0.8" fill="#f59e0b"/>
      </svg>
    ),
    "■": (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}>
        <circle cx="6.5" cy="6.5" r="5" stroke="#ef4444" strokeWidth="1.4" fill="#ef444422"/>
        <path d="M4.5 4.5L8.5 8.5M8.5 4.5L4.5 8.5" stroke="#ef4444" strokeWidth="1.4" strokeLinecap="round"/>
      </svg>
    ),
    "◆": (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}>
        <circle cx="6.5" cy="6.5" r="5" stroke="#22c55e" strokeWidth="1.4" fill="#22c55e22"/>
        <path d="M4 6.5L5.8 8.3L9 5" stroke="#22c55e" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    "▽": (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}>
        <path d="M6.5 2.5V10M3.5 7L6.5 10L9.5 7" stroke="#f87171" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    "▶": (
      <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{flexShrink:0,marginTop:1}}>
        <path d="M6.5 10.5V2.5M3.5 5.5L6.5 2.5L9.5 5.5" stroke="#f59e0b" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  };
  for (const [sym, icon] of Object.entries(ICONS)) {
    if (text.startsWith(sym + " ")) {
      return (
        <span style={{display:"flex",alignItems:"flex-start",gap:5,lineHeight:1.5}}>
          {icon}
          <span>{text.slice(sym.length + 1)}</span>
        </span>
      );
    }
  }
  return <span>{text}</span>;
}

function AnimNum({to,dec=1,suffix="",prefix="",duration=900}) {
  const [val,setVal]=useState(null);
  const ref=useRef();
  useEffect(()=>{
    if(to==null||isNaN(Number(to))){setVal(null);return;}
    const end=Number(to);
    const start=performance.now();
    function tick(now){
      const p=Math.min((now-start)/duration,1);
      const e=1-Math.pow(1-p,3);
      setVal(e*end);
      if(p<1) ref.current=requestAnimationFrame(tick);
    }
    ref.current=requestAnimationFrame(tick);
    return ()=>cancelAnimationFrame(ref.current);
  },[to,duration]);
  if(val===null) return <span style={{color:C.muted}}>N/A</span>;
  return <span>{prefix}{val.toFixed(dec)}{suffix}</span>;
}

/* Chart body + field-engineer Summary toggle for charts that are not inside ChartCard */
function ChartWithSummary({ height, title, subtitle, accent, children }) {
  return (
    <>
      <div style={{ height }}>{children}</div>
      <PlotSummary inline title={title} subtitle={subtitle} accent={accent}>{children}</PlotSummary>
    </>
  );
}

function ChartCard({title,subtitle,explainer,children,height=240,accent=C.cyan}) {
  return (
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:"20px 18px 14px",position:"relative",overflow:"hidden"}}>
      <div style={{position:"absolute",top:0,left:20,right:20,height:2,background:`linear-gradient(to right, transparent, ${accent}88, transparent)`}}/>
      <div style={{marginBottom:12}}>
        <div style={{fontFamily:SANS,fontWeight:700,fontSize:13,color:C.text}}>{title}</div>
        {subtitle&&<div style={{fontFamily:MONO,fontSize:10,color:C.muted,marginTop:2}}>{subtitle}</div>}
        {explainer&&(
          <div style={{marginTop:8,padding:"7px 10px",background:`${accent}08`,border:`1px solid ${accent}22`,borderRadius:6,fontFamily:MONO,fontSize:10,color:accent,lineHeight:1.5}}>
            {explainer}
          </div>
        )}
      </div>
      <div style={{height}}>{children}</div>
      <PlotSummary inline title={title} subtitle={typeof subtitle==="string"?subtitle:""} accent={accent}>{children}</PlotSummary>
    </div>
  );
}

function PanelHeader({ title, subtitle, accent = C.cyan, children }) {
  return (
    <div style={{
      background: C.card,
      border: `1px solid ${C.border}`,
      borderRadius: 14,
      padding: "20px 22px",
      position: "relative",
      overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", top: 0, left: 20, right: 20, height: 2,
        background: `linear-gradient(to right, transparent, ${accent}88, transparent)`,
      }}/>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 13, color: C.text }}>{title}</div>
          {subtitle && <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginTop: 2 }}>{subtitle}</div>}
        </div>
        {children}
      </div>
    </div>
  );
}

function LoadingCard({label}) {
  return (
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:32,display:"flex",alignItems:"center",justifyContent:"center",gap:12}}>
      <div style={{width:16,height:16,border:`2px solid ${C.border2}`,borderTop:`2px solid ${C.cyan}`,borderRadius:"50%",animation:"spin-slow 0.8s linear infinite"}}/>
      <span style={{fontFamily:MONO,fontSize:11,color:C.muted}}>Loading {label}…</span>
    </div>
  );
}

function ErrorCard({label,error}) {
  return (
    <div style={{background:C.card,border:`1px solid ${C.red}33`,borderRadius:14,padding:24}}>
      <div style={{fontFamily:MONO,fontSize:11,color:C.red}}>Failed to load {label}</div>
      <div style={{fontFamily:MONO,fontSize:10,color:C.muted,marginTop:4}}>{error}</div>
    </div>
  );
}

function useFetch(url) {
  const { apiUrl } = useWell();
  const [data,setData]=useState(null);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState(null);
  const { selectedWell } = useWell();
  useEffect(()=>{
    const path = url.replace(API, "").replace("http://localhost:5000/api", "");
    const fullUrl = apiUrl(path);
    setLoading(true);setError(null);
    axios.get(fullUrl)
      .then(r=>{setData(r.data);setLoading(false);})
      .catch(e=>{setError(e.message);setLoading(false);});
  },[url, selectedWell]);
  return {data,loading,error};
}

/* ROOT CAUSE ANALYSIS */
function RootCausePanel({winQs,onTimes}) {
  const [window,setWindow]=useState(10);
  // no window -> exactly the old call (last N vs previous N cycles); a window -> recent = cycles inside it, previous = equally long period before
  const {data,loading,error}=useFetch(`${API}/insights/root-cause?${winQs ? winQs : `window=${window}`}`);
  useEffect(()=>{ if(data?.cycle_times?.length) onTimes(data.cycle_times.map(t=>({time:t,_t:parseT(t)}))); },[data]);   // eslint-disable-line react-hooks/exhaustive-deps
  if(loading) return <LoadingCard label="Root Cause Analysis"/>;
  if(error && winQs) return (
    <div style={{background:C.card,border:`1px solid ${C.amber}33`,borderRadius:14,padding:24,fontFamily:MONO,fontSize:12,color:C.amber}}>
      Not enough cycles in this window and the equally long period before it to compare, widen the window.
    </div>
  );
  if(error)   return <ErrorCard label="Root Cause Analysis" error={error}/>;
  const {window_cycles,production_change_pct,production_direction,top_factors,interpretations}=data;
  const rr = data.recent_range, pr = data.previous_range;
  const rangeTxt = (r) => (r && parseT(r[0])!=null && parseT(r[1])!=null) ? `${fmtDT(parseT(r[0]))} → ${fmtDT(parseT(r[1]))}` : null;
  const DIR_COLOR={improved:C.green,declined:C.red,stable:C.teal,unknown:C.muted};
  const dirColor=DIR_COLOR[production_direction]||C.muted;
  const chartData=(top_factors||[]).slice(0,6).map(f=>{
    const sig = f.significance;
    const status = sig == null ? "good" : sig > 1.5 ? "critical" : sig > 0.8 ? "warning" : "good";
    return {
      name:f.feature.replace(/_/g," ").replace(/([A-Z])/g," $1").trim().split(" ").slice(-2).join(" "),
      feature:f.feature,
      significance:sig,
      pct_change:f.pct_change,
      direction:f.direction,
      status,
      _actualFieldKey:"significance",
    };
  });

  return (
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:PLOT_GAP_PX}}>
      <div style={{gridColumn:"1 / -1"}}>
        <PanelHeader
          title="Root Cause Analysis"
          subtitle={winQs
            ? `Recent ${window_cycles} cycles${rangeTxt(rr)?` (${rangeTxt(rr)})`:""} vs previous ${data.previous_cycles} cycles${rangeTxt(pr)?` (${rangeTxt(pr)})`:""}`
            : `Comparing recent ${window_cycles} cycles vs previous ${window_cycles} cycles${rangeTxt(rr)&&rangeTxt(pr)?` · recent ${rangeTxt(rr)} · previous ${rangeTxt(pr)}`:""}`}
          accent={C.purple}
        >
          <div style={{display:"flex",gap:8}}>
            {!winQs && [5,10,15].map(v=>(
              <button key={v} onClick={()=>setWindow(v)} style={{fontFamily:MONO,fontSize:10,padding:"5px 12px",borderRadius:6,border:`1px solid ${window===v?C.cyan+"66":C.border}`,background:window===v?`${C.cyan}15`:"transparent",color:window===v?C.cyan:C.muted,cursor:"pointer"}}>{v} cycles</button>
            ))}
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontFamily:MONO,fontSize:10,color:C.muted}}>PRODUCTION CHANGE</div>
            <div style={{fontFamily:MONO,fontSize:32,fontWeight:700,color:dirColor}}>
              {production_change_pct!=null?`${production_change_pct>=0?"+":""}${production_change_pct}%`:"N/A"}
            </div>
            <div style={{fontFamily:MONO,fontSize:11,color:dirColor,textTransform:"capitalize"}}>{production_direction}</div>
          </div>
        </PanelHeader>
      </div>

      <ChartCard title="Most Changed Parameters" subtitle="Significance = standard deviations shifted" accent={C.purple} height={220}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} layout="vertical" margin={{top:5,right:20,left:5,bottom:0}}>
            <CartesianGrid stroke={C.border} strokeDasharray="3 3" horizontal={false}/>
            <XAxis type="number" tick={{fill:C.muted,fontSize:9,fontFamily:MONO}} tickLine={false} axisLine={false}/>
            <YAxis type="category" dataKey="name" tick={{fill:C.muted,fontSize:9,fontFamily:MONO}} tickLine={false} width={80}/>
            <Tooltip content={<SmartTooltip note="Status: GOOD ≤0.8σ · WARNING >0.8 · CRITICAL >1.5, color matches hover class"/>}/>
            <Bar dataKey="significance" name="Significance (σ)" radius={[0,4,4,0]}>
              {chartData.map((entry,i)=><Cell key={i} fill={entry.significance>1.5?C.red:entry.significance>0.8?C.amber:C.green} fillOpacity={0.85}/>)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:"20px 18px"}}>
        <div style={{fontFamily:SANS,fontWeight:700,fontSize:13,color:C.text,marginBottom:14}}>AI Interpretation</div>
        {(interpretations||[]).length===0?(
          <div style={{fontFamily:MONO,fontSize:11,color:C.muted}}>No significant changes detected in this window.</div>
        ):(
          interpretations.map((interp,i)=>(
            <div key={i} style={{marginBottom:14,padding:"12px 14px",background:`${C.purple}08`,border:`1px solid ${C.purple}22`,borderRadius:10}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                <span style={{fontFamily:MONO,fontSize:9,color:C.purple,background:`${C.purple}22`,padding:"2px 8px",borderRadius:4,textTransform:"uppercase"}}>Factor {i+1}</span>
                <span style={{fontFamily:MONO,fontSize:9,color:C.muted}}>σ = {interp.significance?.toFixed(2)}</span>
              </div>
              <div style={{fontFamily:MONO,fontSize:11,color:C.text,lineHeight:1.6}}>{String(interp.message || "").replace(/\s*—\s*/g, " ").trim()}</div>
            </div>
          ))
        )}
      </div>

      <div style={{gridColumn:"1 / -1",background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:"20px 18px"}}>
        <div style={{fontFamily:SANS,fontWeight:700,fontSize:13,color:C.text,marginBottom:14}}>All Parameters, Baseline vs Current</div>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontFamily:MONO,fontSize:11}}>
            <thead>
              <tr style={{borderBottom:`2px solid ${C.border2}`}}>
                {["Parameter","Baseline Avg","Current Avg","Change %","How Much It Shifted (σ)","Direction"].map(h=>(
                  <th key={h} style={{padding:"8px 12px",textAlign:"left",color:C.muted,fontWeight:400,fontSize:10}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(top_factors||[]).map((f,i)=>{
                const sig=f.significance;
                const sigColor=sig>1.5?C.red:sig>0.8?C.amber:C.green;
                const dirC=f.direction==="increased"?C.green:C.red;
                return (
                  <tr key={i} style={{borderBottom:`1px solid ${C.border}`}}>
                    <td style={{padding:"8px 12px",color:C.text}}>{f.feature}</td>
                    <td style={{padding:"8px 12px",color:C.muted}}>{f.prev_mean}</td>
                    <td style={{padding:"8px 12px",color:C.text}}>{f.recent_mean}</td>
                    <td style={{padding:"8px 12px",color:dirC}}>{f.pct_change>=0?"+":""}{f.pct_change}%</td>
                    <td style={{padding:"8px 12px",color:sigColor}}>
                      <span style={{display:"flex",alignItems:"center",gap:6}}>
                        {f.significance.toFixed(2)}
                        <div style={{height:4,width:Math.min(f.significance*20,60),background:sigColor,borderRadius:2,opacity:0.7}}/>
                      </span>
                    </td>
                    <td style={{padding:"8px 12px"}}>
                      <span style={{background:`${dirC}20`,color:dirC,padding:"2px 8px",borderRadius:4,fontSize:10}}>{f.direction}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
/* ───────────── TIME-WINDOW HELPERS (same bar / axis / look as Overview, AI Controller, GLR) ───────────── */
const MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const pad2 = n => String(n).padStart(2, "0");
const HOUR = 3600000, DAY = 86400000;

// backend sends "YYYY-MM-DD HH:MM" - parse as plain local clock time (no timezone shifting)
function parseT(s) {
  if (!s) return null;
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]).getTime() : null;
}
const fmtDT   = ms => { const d = new Date(ms); return `${d.getDate()} ${MON[d.getMonth()]} ${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
const fmtD    = ms => { const d = new Date(ms); return `${d.getDate()} ${MON[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`; };
const fmtHM   = ms => { const d = new Date(ms); return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
const toInput = ms => { const d = new Date(ms); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; };
const toQuery = ms => `${toInput(ms)} ${fmtHM(ms)}`;

function fmtSpan(ms) {
  if (ms < HOUR)      return `${Math.max(1, Math.round(ms / 60000))} min`;
  if (ms < 2 * DAY)   return `${Math.round(ms / HOUR)} h`;
  const d = Math.round(ms / DAY);
  return d >= 14 ? `${d} days (${(d / 7).toFixed(1)} wk)` : `${d} days`;
}
function fmtAgo(ms) {
  if (ms < HOUR)    return "just now";
  if (ms < 2 * DAY) return `${Math.round(ms / HOUR)} h ago`;
  return `${Math.round(ms / DAY)} d ago`;
}

const PRESETS = [
  { id: "24h",   label: "24 H",   ms: DAY },
  { id: "7d",    label: "7 D",    ms: 7 * DAY },
  { id: "30d",   label: "30 D",   ms: 30 * DAY },
  { id: "90d",   label: "90 D",   ms: 90 * DAY },
  { id: "cyc30", label: "LAST 30 CYC", cycles: 30 },
  { id: "all",   label: "ALL" },
];
const MIN_POINTS = 3;   // fewer cycles than this cannot draw a meaningful trend

/* Apply a window to ANY array of rows that carry `_t` (ms). Presets are measured back from the LATEST cycle in the data, not from "now". */
function applyWin(w, tMax, arr) {
  if (w.mode === "all" || tMax == null) return arr;
  if (w.mode === "cyc30") {
    if (arr.some(r => r.well != null)) {            // multi-well rows -> newest 30 of EACH well
      const by = {};
      arr.forEach(r => { (by[r.well] = by[r.well] || []).push(r); });
      return Object.values(by).flatMap(a => a.slice(-30));
    }
    return arr.slice(-30);
  }
  if (w.mode === "custom") {
    const f = w.from ? parseT(`${w.from} 00:00`) : -Infinity;
    const t = w.to   ? parseT(`${w.to} 23:59`)   :  Infinity;
    return arr.filter(d => d._t != null && d._t >= f && d._t <= t);
  }
  const p = PRESETS.find(x => x.id === w.mode);
  return arr.filter(d => d._t != null && d._t >= tMax - p.ms);
}
/* `rows` = every cycle time the window bar should know about ({_t, well?}). `filter(arr)` windows any per-well / per-view array the same way. */
function useTimeWindow(rows) {
  const [win, setWin] = useState({ mode: "all", from: "", to: "" });
  const tMin = useMemo(() => { const v = rows.map(d => d._t).filter(t => t != null); return v.length ? Math.min(...v) : null; }, [rows]);
  const tMax = useMemo(() => { const v = rows.map(d => d._t).filter(t => t != null); return v.length ? Math.max(...v) : null; }, [rows]);
  const view = useMemo(() => applyWin(win, tMax, rows), [rows, win, tMax]);
  const presetCounts = useMemo(() => Object.fromEntries(PRESETS.map(p => [p.id, applyWin({ mode: p.id }, tMax, rows).length])), [rows, tMax]);
  return { win, setWin, view, presetCounts, tMin, tMax, isAll: win.mode === "all", filter: arr => applyWin(win, tMax, arr) };
}

/* window -> query string for backend endpoints that window server-side (no params = unchanged behaviour) */
function winQuery(win, tMax) {
  if (win.mode === "all" || tMax == null) return "";
  if (win.mode === "cyc30") return "window=30";
  if (win.mode === "custom") {
    const p = [];
    if (win.from) p.push(`from=${encodeURIComponent(win.from + " 00:00")}`);
    if (win.to)   p.push(`to=${encodeURIComponent(win.to + " 23:59")}`);
    return p.join("&");
  }
  const pr = PRESETS.find(x => x.id === win.mode);
  return `from=${encodeURIComponent(toQuery(tMax - pr.ms))}&to=${encodeURIComponent(toQuery(tMax))}`;
}

/* Second row of the X axis = timestamp of the cycle sitting at that position. */
function DateTick({ x, y, payload, index, lookup, lastIdx, shortSpan }) {
  const t = lookup.get(payload.value);
  if (t == null) return null;
  const anchor = index === 0 ? "start" : index === lastIdx ? "end" : "middle";
  const l1 = shortSpan ? fmtHM(t) : fmtD(t);
  const l2 = shortSpan ? fmtD(t)  : fmtHM(t);
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor={anchor} fill={C.amber} fontSize={9} fontFamily={MONO} dy={11}>{l1}</text>
      <text textAnchor={anchor} fill={C.muted} fontSize={8} fontFamily={MONO} dy={22}>{l2}</text>
    </g>
  );
}

/* Call as a function (NOT <Component/>) - recharts only recognises <XAxis> as a direct child.
   Rows must carry `key` (default "index") and `_t`. labelText = the "Cycle Number →" caption, moved under the timestamp row. */
function dateXAxis(rows, key = "index", nTicks = 5, labelText, opts = {}) {
  const stamped = rows.filter(r => r._t != null);
  if (!stamped.length) return null;
  const last = stamped.length - 1;
  const idx = new Set();
  for (let i = 0; i < nTicks; i++) idx.add(Math.round((i * last) / Math.max(nTicks - 1, 1)));
  const ticks = [...idx].filter(i => stamped[i]).map(i => stamped[i][key]);
  const lookup = new Map(stamped.map(r => [r[key], r._t]));
  const span = stamped.length > 1 ? (stamped[last]._t - stamped[0]._t) : 0;
  return (
    <XAxis xAxisId="date" dataKey={key} ticks={ticks} interval={0} tickLine={false} axisLine={false} height={opts.height || 32}
      tick={<DateTick lookup={lookup} lastIdx={ticks.length - 1} shortSpan={span > 0 && span < 3 * DAY} />}
      label={labelText ? { value: labelText, position: "insideBottom", offset: opts.labelOffset ?? -14, fill: C.muted, fontSize: 9, fontFamily: MONO } : undefined} />
  );
}
const hasStamps = rows => rows.some(r => r._t != null);

function DataWindowBar({ ctl, total, note, isDark }) {
  const { win, setWin, view, presetCounts, tMin, tMax, isAll } = ctl;
  const vs      = view.filter(d => d._t != null);
  const vStart  = vs.length ? Math.min(...vs.map(d => d._t)) : null;
  const vEnd    = vs.length ? Math.max(...vs.map(d => d._t)) : null;
  const hasTime = vStart != null && vEnd != null;
  const spanMs  = hasTime ? Math.max(vEnd - vStart, 0) : 0;
  const tooFew  = view.length < MIN_POINTS;
  const latestAgo = tMax != null ? Date.now() - tMax : null;
  const btn = (active, disabled) => ({
    fontFamily: MONO, fontSize: 10, letterSpacing: 1, padding: "5px 11px", borderRadius: 6, cursor: disabled ? "not-allowed" : "pointer",
    background: active ? `${C.amber}22` : "transparent", color: disabled ? C.muted2 : active ? C.amber : C.muted,
    border: `1px solid ${active ? C.amber : C.border2}`, opacity: disabled ? 0.55 : 1, whiteSpace: "nowrap",
  });
  const dateInput = { fontFamily: MONO, fontSize: 10, background: C.panel, color: C.text, border: `1px solid ${C.border2}`, borderRadius: 6, padding: "4px 8px", colorScheme: isDark ? "dark" : "light" };

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.amber}`, borderRadius: 12, padding: "14px 18px" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: 2, color: C.muted }}>DATA WINDOW</span>
        <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.text }}>
          {hasTime ? `${fmtDT(vStart)}  →  ${fmtDT(vEnd)}` : "no timestamps available"}
        </span>
        {hasTime && <span style={{ fontFamily: MONO, fontSize: 10, color: C.amber, background: `${C.amber}14`, border: `1px solid ${C.amber}33`, borderRadius: 5, padding: "2px 8px" }}>
          {isAll ? "CUMULATIVE" : "WINDOW"} · {fmtSpan(spanMs)}
        </span>}
        <span style={{ fontFamily: MONO, fontSize: 10, color: C.cyan, background: `${C.cyan}12`, border: `1px solid ${C.cyan}33`, borderRadius: 5, padding: "2px 8px" }}>
          {view.length} of {total} cycles
        </span>
        {latestAgo != null && (
          <span title="Presets are measured back from the newest cycle in the data, not from today's date"
            style={{ fontFamily: MONO, fontSize: 10, color: latestAgo > DAY ? C.orange : C.green, background: `${latestAgo > DAY ? C.orange : C.green}12`, border: `1px solid ${latestAgo > DAY ? C.orange : C.green}33`, borderRadius: 5, padding: "2px 8px" }}>
            Latest cycle {fmtDT(tMax)} · {fmtAgo(latestAgo)}
          </span>
        )}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
        {PRESETS.map(p => {
          const n = presetCounts[p.id];
          const disabled = p.id !== "all" && n < MIN_POINTS;
          return (
            <button key={p.id} disabled={disabled} onClick={() => setWin({ mode: p.id, from: "", to: "" })}
              title={disabled ? `Only ${n} cycle${n === 1 ? "" : "s"} in this window, too few to plot a trend. Try a wider window.` : `${n} cycles`}
              style={btn(win.mode === p.id, disabled)}>
              {p.label} <span style={{ opacity: 0.7 }}>· {n}</span>
            </button>
          );
        })}
        <button onClick={() => setWin(w => ({ mode: "custom", from: w.from || (tMin != null ? toInput(tMin) : ""), to: w.to || (tMax != null ? toInput(tMax) : "") }))}
          style={btn(win.mode === "custom", false)}>CUSTOM RANGE</button>
        {win.mode === "custom" && (
          <>
            <input type="date" value={win.from} min={tMin != null ? toInput(tMin) : undefined} max={win.to || (tMax != null ? toInput(tMax) : undefined)}
              onChange={e => setWin(w => ({ ...w, from: e.target.value }))} style={dateInput} />
            <span style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>to</span>
            <input type="date" value={win.to} min={win.from || (tMin != null ? toInput(tMin) : undefined)} max={tMax != null ? toInput(tMax) : undefined}
              onChange={e => setWin(w => ({ ...w, to: e.target.value }))} style={dateInput} />
          </>
        )}
      </div>

      
      {tooFew && (
        <div style={{ fontFamily: MONO, fontSize: 10, color: C.orange, marginTop: 8 }}>
          ⚠ Only {view.length} cycle{view.length === 1 ? "" : "s"} in this range, widen the window to see a trend.
        </div>
      )}
    </div>
  );
}


const shortWell = w => String(w).split("/").pop();


function KpiRow({items}) {
  return (
    <div style={{display:"grid",gridTemplateColumns:`repeat(${items.length}, 1fr)`,gap:14}}>
      {items.map(s=>(
        <div key={s.label} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:"16px 18px",borderTop:`3px solid ${s.color}`}}>
          <div style={{fontFamily:MONO,fontSize:9,color:C.muted,letterSpacing:1.5,textTransform:"uppercase",marginBottom:6}}>{s.label}</div>
          <div style={{fontFamily:MONO,fontSize:26,fontWeight:700,color:s.color}}><AnimNum to={s.value} dec={0}/></div>
          <div style={{fontFamily:MONO,fontSize:9,color:C.muted,marginTop:4}}>{s.tip}</div>
        </div>
      ))}
    </div>
  );
}

function Panel({title,subtitle,children,style}) {
  return (
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:"20px 18px",...style}}>
      <div style={{fontFamily:SANS,fontWeight:700,fontSize:13,color:C.text,marginBottom:4}}>{title}</div>
      {subtitle&&<div style={{fontFamily:MONO,fontSize:10,color:C.muted,marginBottom:14}}>{subtitle}</div>}
      {children}
    </div>
  );
}

function Chip({color,children}) {
  return <span style={{display:"inline-block",background:`${color}22`,color,padding:"2px 8px",borderRadius:4,fontSize:9,marginRight:5,marginBottom:2,fontFamily:MONO}}>{children}</span>;
}


/* ═════════ ROOT CAUSE MATRIX (sub-tabs inside the Root Cause tab) ═════════ */
const RCA_SUBTABS = [
  {id:"change",     label:"Change Analysis",       desc:"What changed (existing)"},
  {id:"overview",   label:"Symptom Overview",      desc:"12 areas · live status"},
  {id:"plunger",    label:"Plunger & Cycle",       desc:"Arrival · velocity · timing"},
  {id:"pressure",   label:"Pressure & Liquid",     desc:"Casing · tubing · loading"},
  {id:"production", label:"Production & Equipment",desc:"Output · injection · surface"},
];
const RCA_STATUS = {
  active:  {label:"ACTIVE",  color:()=>C.red},
  watch:   {label:"WATCH",   color:()=>C.amber},
  normal:  {label:"NORMAL",  color:()=>C.green},
  no_data: {label:"NO DATA", color:()=>C.muted},
};
const rcaColor = s => (RCA_STATUS[s]||RCA_STATUS.no_data).color();

function RootCauseSection() {
  const { isDark } = useTheme();
  const [rsub,setRsub] = useState(()=>sessionStorage.getItem("rootCauseSubTab")||"change");
  const pick = id => { setRsub(id); sessionStorage.setItem("rootCauseSubTab",id); };
  // every cycle time (reported by whichever view is loaded) -> shared DATA WINDOW bar for all five sub-tabs
  const [times,setTimes] = useState([]);
  const reportTimes = useCallback(rows => setTimes(prev => (prev.length===rows.length && prev[0]?.time===rows[0]?.time && prev[prev.length-1]?.time===rows[rows.length-1]?.time) ? prev : rows),[]);
  const tw = useTimeWindow(times);
  const winQs = useMemo(()=>winQuery(tw.win,tw.tMax),[tw.win,tw.tMax]);
  return (
    <div style={{display:"flex",flexDirection:"column",gap:PLOT_GAP_PX}}>
      <div style={{display:"flex",gap:12,flexWrap:"wrap",paddingBottom:18,marginBottom:4,borderBottom:`1px solid ${C.border}`}}>
        {RCA_SUBTABS.map(t=>(
          <button key={t.id} onClick={()=>pick(t.id)} style={{
            fontFamily:MONO,fontSize:11,padding:"10px 18px",borderRadius:8,cursor:"pointer",minWidth:170,textAlign:"left",
            border:`1px solid ${rsub===t.id?C.cyan+"66":C.border}`,
            background:rsub===t.id?`${C.cyan}14`:"transparent",
            color:rsub===t.id?C.cyan:C.muted,display:"flex",flexDirection:"column",alignItems:"flex-start",gap:5,
          }}>
            <span style={{fontWeight:700}}>{t.label}</span>
            <span style={{fontSize:9,lineHeight:1.4,color:rsub===t.id?C.cyan:C.muted}}>{t.desc}</span>
          </button>
        ))}
      </div>
      {times.length>0 && <DataWindowBar ctl={tw} total={times.length} isDark={isDark}/>} 
      {rsub==="change" ? <RootCausePanel winQs={winQs} onTimes={reportTimes}/> : <RcaMatrix view={rsub} onPick={pick} winQs={winQs} onTimes={reportTimes}/>}
    </div>
  );
}

function RcaMatrix({view,onPick,winQs,onTimes}) {
  const {data,loading,error}=useFetch(`${API}/insights/root-cause/matrix${winQs?`?${winQs}`:""}`);
  const [well,setWell]=useState(null);
  useEffect(()=>{
    if(!data?.wells) return;
    const rows = Object.entries(data.wells).flatMap(([id,w])=>(w.cycle_times||[]).map(t=>({well:id,time:t,_t:parseT(t)})));
    if(rows.length) onTimes(rows);
  },[data]);   // eslint-disable-line react-hooks/exhaustive-deps
  if(loading) return <LoadingCard label="Root-cause matrix"/>;
  if(error)   return <ErrorCard label="Root-cause matrix" error={error}/>;
  const ids=Object.keys(data.wells);
  const active=well&&data.wells[well]?well:ids[0];
  const w=data.wells[active];
  return (
    <div style={{display:"flex",flexDirection:"column",gap:PLOT_GAP_PX}}>
      {ids.length>1 && (
        <div style={{display:"flex",gap:6,flexWrap:"wrap",fontFamily:MONO,fontSize:10,alignItems:"center"}}>
          <span style={{color:C.muted}}>Well:</span>
          {ids.map(id=>(
            <button key={id} onClick={()=>setWell(id)} style={{fontFamily:MONO,fontSize:10,padding:"3px 9px",borderRadius:6,cursor:"pointer",
              border:`1px solid ${active===id?C.cyan+"66":C.border}`,background:active===id?`${C.cyan}14`:"transparent",color:active===id?C.cyan:C.muted}}>{shortWell(id)}</button>))}
        </div>
      )}
      {view==="overview"
        ? <RcaOverview data={data} w={w} onPick={onPick}/>
        : <div style={{display:"flex",flexDirection:"column",gap:PLOT_GAP_PX}}>
            {w.areas.filter(a=>a.tab===view && a.status!=="no_data").map(a=><RcaAreaCard key={a.id} a={a} w={w}/>)}
          </div>}
    </div>
  );
}

function RcaOverview({data,w,onPick}) {
  const cnt=w.areas.reduce((m,a)=>{m[a.status]=(m[a.status]||0)+1;return m;},{});
  const ids=Object.keys(data.wells);
  const tabOf = {plunger:"plunger",pressure:"pressure",production:"production"};
  return (
    <div style={{display:"flex",flexDirection:"column",gap:PLOT_GAP_PX}}>
      <KpiRow items={[
        {label:"Active Symptoms",value:cnt.active||0,color:C.red,tip:"Symptom strongly present"},
        {label:"Watch",value:cnt.watch||0,color:C.amber,tip:"Early / moderate signs"},
        {label:"Normal",value:cnt.normal||0,color:C.green,tip:"No symptom in recent window"},
        {label:"No Data",value:cnt.no_data||0,color:C.muted,tip:"Needed channels not available"},
      ]}/>
      <Panel title="Symptom → Root-Cause Matrix" subtitle={`Well ${shortWell(w.well_id)} · click a row to open its detail tab`}>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontFamily:MONO,fontSize:11}}>
            <thead><tr style={{borderBottom:`2px solid ${C.border2}`}}>
              {["Area","Symptom","Status","Most likely cause","Other candidate causes"].map(h=>(
                <th key={h} style={{padding:"8px 12px",textAlign:"left",color:C.muted,fontWeight:400,fontSize:10}}>{h}</th>))}
            </tr></thead>
            <tbody>{w.areas.filter(a=>a.status!=="no_data").map(a=>{
              const live=a.status==="active"||a.status==="watch";
              const causes=Array.isArray(a.causes)?a.causes:[];
              const top=live?causes[0]:null;
              return (
                <tr key={a.id} onClick={()=>onPick(tabOf[a.tab])} style={{borderBottom:`1px solid ${C.border}`,cursor:"pointer"}}>
                  <td style={{padding:"8px 12px",color:C.text,whiteSpace:"nowrap"}}>{a.area}</td>
                  <td style={{padding:"8px 12px",color:C.muted,maxWidth:230}}>{a.symptom}</td>
                  <td style={{padding:"8px 12px"}}><Chip color={rcaColor(a.status)}>{RCA_STATUS[a.status].label}</Chip></td>
                  <td style={{padding:"8px 12px",color:C.text}}>{top&&top.support>=0.2?`${top.cause} (${Math.round(top.support*100)}%)`:"N/A"}</td>
                  <td style={{padding:"8px 12px",color:C.muted,maxWidth:300}}>
                    {causes.filter((c,i)=>!(live&&i===0)).map(c=>c.cause).join(" · ")}
                  </td>
                </tr>);
            })}</tbody>
          </table>
        </div>
      </Panel>
      {ids.length>1 && (
        <Panel title="All Wells" subtitle="Status of every area on every well">
          <div style={{overflowX:"auto"}}>
            <table style={{borderCollapse:"collapse",fontFamily:MONO,fontSize:10}}>
              <thead><tr>
                <th style={{padding:"6px 10px",textAlign:"left",color:C.muted,fontWeight:400}}>Well</th>
                {data.area_meta.map(a=><th key={a.id} style={{padding:"6px 6px",color:C.muted,fontWeight:400,fontSize:9,maxWidth:70}}>{a.id.replace(/_/g," ")}</th>)}
              </tr></thead>
              <tbody>{ids.map(id=>(
                <tr key={id} style={{borderTop:`1px solid ${C.border}`}}>
                  <td style={{padding:"6px 10px",color:C.text}}>{shortWell(id)}</td>
                  {data.area_meta.map(a=>{
                    const st=data.portfolio[id][a.id];
                    return <td key={a.id} title={`${a.id}: ${st}`} style={{padding:"6px",textAlign:"center"}}>
                      <span style={{display:"inline-block",width:12,height:12,borderRadius:3,background:`${rcaColor(st)}${st==="normal"?"44":"cc"}`}}/>
                    </td>;
                  })}
                </tr>))}</tbody>
            </table>
          </div>
        </Panel>
      )}
    </div>
  );
}

function RcaAreaCard({a,w}) {
  const live=a.status==="active"||a.status==="watch";
  const [open,setOpen]=useState(live);
  const col=rcaColor(a.status);
  const keys=a.chart.keys;
  const palette=[C.cyan,C.muted,C.purple];
  return (
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,borderLeft:`3px solid ${col}`,overflow:"hidden"}}>
      <div onClick={()=>setOpen(!open)} style={{padding:"16px 18px",cursor:"pointer",display:"flex",justifyContent:"space-between",gap:14,alignItems:"flex-start",flexWrap:"wrap"}}>
        <div style={{flex:1,minWidth:240}}>
          <div style={{fontFamily:SANS,fontWeight:700,fontSize:13,color:C.text}}>{a.area}: {a.symptom}</div>
          <div style={{fontFamily:MONO,fontSize:10,color:C.muted,marginTop:3}}>{a.detail}</div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          {a.score!=null && <span style={{fontFamily:MONO,fontSize:10,color:C.muted}}>symptom {Math.round(a.score*100)}%</span>}
          <Chip color={col}>{RCA_STATUS[a.status].label}</Chip>
          <span style={{color:C.muted,fontFamily:MONO,fontSize:11}}>{open?"▲":"▼"}</span>
        </div>
      </div>
      {open && (
        <div style={{padding:"0 18px 18px",display:"flex",flexDirection:"column",gap:PLOT_GAP_PX}}>
          {a.metrics.length>0 && (
            <div style={{display:"grid",gridTemplateColumns:`repeat(${Math.min(a.metrics.length,5)}, 1fr)`,gap:10}}>
              {a.metrics.map(m=>(
                <div key={m.label} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 10px"}}>
                  <div style={{fontFamily:MONO,fontSize:9,color:C.muted}}>{m.label}</div>
                  <div style={{fontFamily:MONO,fontSize:15,fontWeight:700,color:C.text}}>{m.recent==null?"N/A":m.recent}<span style={{fontSize:9,color:C.muted}}> {m.unit}</span></div>
                  <div style={{fontFamily:MONO,fontSize:9,color:m.change_pct==null?C.muted2:Math.abs(m.change_pct)>=20?C.amber:C.muted}}>
                    {m.baseline!=null?`baseline ${m.baseline}`:""}{m.change_pct!=null?` · ${m.change_pct>0?"+":""}${m.change_pct}%`:""}
                  </div>
                </div>))}
            </div>
          )}
          {keys.length>0 && (() => {
            const chartRows = (w.series || []).map(row => ({ ...row, time: row.t ? String(row.t).slice(0,16) : undefined, _t: parseT(row.t), _actualFieldKey: keys[0] || undefined }));
            const hasT = hasStamps(chartRows);
            const hasVariation = keys.some((k) => {
              const vals = chartRows.map((r) => r[k]).filter((v) => v != null && Number.isFinite(Number(v)));
              if (vals.length < 3) return false;
              const min = Math.min(...vals.map(Number));
              const max = Math.max(...vals.map(Number));
              return max - min > 1e-6;
            });
            if (!hasVariation) {
              return (
                <div style={{fontFamily:MONO,fontSize:11,color:C.muted,padding:"8px 0"}}>
                  Trend chart hidden, selected channel has no meaningful variation in this window.
                </div>
              );
            }
            return (
            <ChartWithSummary height={hasT?205:170} accent={col} title={`${a.area}: ${a.chart.title}`} subtitle={`${a.symptom}. ${a.chart.title} over the last ${w.series.length} cycles; dashed line marks the start of the recent window`}>
              <div style={{fontFamily:MONO,fontSize:10,color:C.muted,marginBottom:4}}>{a.chart.title} · last {w.series.length} cycles · dashed line = start of recent window</div>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartRows} margin={{top:28,right:16,left:0,bottom:hasT?20:5}}>
                  <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false}/>
                  <XAxis dataKey="i" tick={{fill:C.muted,fontSize:9,fontFamily:MONO}} tickLine={false} height={hasT?20:undefined}/>
                  {dateXAxis(chartRows,"i",5,"Cycle Number →")}
                  <YAxis tick={{fill:C.muted,fontSize:9,fontFamily:MONO}} tickLine={false} axisLine={false} width={42}/>
                  <Tooltip content={<SmartTooltip note="Hover for status and AI recommendation"/>}/>
                  <Legend verticalAlign="top" height={28} wrapperStyle={{fontFamily:MONO,fontSize:10,paddingBottom:4}}/>
                  <ReferenceLine x={w.window_start} stroke={C.amber+"88"} strokeDasharray="4 3"/>
                  {keys.map((k,i)=><Line key={k} type="monotone" dataKey={k} name={seriesLabel(k)} stroke={palette[i%3]} strokeWidth={1.6} dot={false} connectNulls/>)}
                </LineChart>
              </ResponsiveContainer>
            </ChartWithSummary>
            );
          })()}
          {a.status==="no_data" ? (
            <div style={{fontFamily:MONO,fontSize:11,color:C.muted}}>{a.detail}</div>
          ) : a.status==="normal" ? null : (
            <div>
              <div style={{fontFamily:MONO,fontSize:10,color:C.muted,marginBottom:8}}>POSSIBLE ROOT CAUSES · evidence strength from this well's data</div>
              {a.causes.filter(c=>c.support>=0.05).map((c,i)=>(
                <div key={c.cause} style={{padding:"10px 0",borderTop:i?`1px solid ${C.border}`:"none"}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <span style={{width:200,fontFamily:MONO,fontSize:11,color:c.support>=0.2?C.text:C.muted}}>{c.cause}</span>
                    <div style={{flex:1,height:6,background:C.border,borderRadius:3,overflow:"hidden"}}>
                      <div style={{width:`${c.support*100}%`,height:"100%",background:c.support>=0.5?C.red:c.support>=0.2?C.amber:C.muted2}}/>
                    </div>
                    <span style={{width:36,textAlign:"right",fontFamily:MONO,fontSize:10,color:C.muted}}>{Math.round(c.support*100)}%</span>
                    {c.indirect && <Chip color={C.purple}>needs field check</Chip>}
                  </div>
                  {c.support>=0.2 && (
                    <div style={{marginTop:6,paddingLeft:2,fontFamily:MONO,fontSize:10,lineHeight:1.6}}>
                      {c.evidence.map((e,j)=><div key={j} style={{color:C.text}}>• {e}</div>)}
                      <div style={{color:C.cyan,marginTop:3}}>Action: {c.action}</div>
                    </div>
                  )}
                </div>))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


export default function RootCausePage() {
  const { isDark } = useTheme();
  applyTheme(getColors(isDark));
  return (
    <div>
      <RootCauseSection/>
    </div>
  );
}